import React, { useState, useMemo } from "react";
import { Helmet } from "react-helmet";
import Main from "../../layout/Main";
import { Link } from "react-router-dom";

const privacySections = [
  {
    id: "section-1",
    num: "1",
    title: "Introduction",
    content: (
      <div className="space-y-4">
        <p>
          Entrusted Limited (&quot;Entrusted&quot;, &quot;we&quot;, &quot;us&quot; or &quot;our&quot;) is committed to protecting the privacy, security and confidentiality of the personal data entrusted to us by our customers, beneficiaries, agents, corporate users and other persons who interact with the Entrusted Remittance Application, website and related services (collectively, the &quot;Platform&quot; or &quot;Services&quot;).
        </p>
        <p>
          This Privacy Policy explains how Entrusted collects, uses, stores, shares, retains and protects personal data when you register for, access or use our Platform or otherwise interact with us.
        </p>
        <p>
          Entrusted provides inbound international money remittance services that enable eligible customers to send money to beneficiaries in Nigeria, subject to applicable laws, CBN regulatory requirements, Anti-Money Laundering / Counter-Terrorist Financing / Counter-Proliferation Financing (AML/CFT/CPF) obligations, and our Terms of Use.
        </p>
        <p>
          By registering for or using the Platform, you acknowledge that you have read, understood and agree to the processing of your personal data as described in this Privacy Policy.
        </p>
      </div>
    ),
  },
  {
    id: "section-2",
    num: "2",
    title: "Who We Are",
    content: (
      <div className="space-y-4">
        <div className="grid sm:grid-cols-3 gap-4 bg-primary-50 p-4 rounded-xl border border-primary-100">
          <div>
            <span className="text-xs text-primary-700 uppercase font-bold block">Data Controller</span>
            <span className="font-semibold text-primary-950">Entrusted Limited</span>
          </div>
          <div>
            <span className="text-xs text-primary-700 uppercase font-bold block">Business Activity</span>
            <span className="font-semibold text-primary-950">International Money Transfer / Remittance Services (Inbound)</span>
          </div>
          <div>
            <span className="text-xs text-primary-700 uppercase font-bold block">Jurisdiction</span>
            <span className="font-semibold text-primary-950">Federal Republic of Nigeria</span>
          </div>
        </div>
        <p>
          Entrusted acts as a Data Controller in respect of personal data for which it determines the purposes and means of processing. Where Entrusted engages third-party service providers to process personal data on its behalf, such providers act as Data Processors and are contractually required to process information only in accordance with applicable Nigerian data protection law, CBN requirements and Entrusted&apos;s documented instructions.
        </p>
      </div>
    ),
  },
  {
    id: "section-3",
    num: "3",
    title: "Information We Collect",
    content: (
      <div className="space-y-6">
        <div>
          <h4 className="font-semibold text-primary-500 mb-2">3.1 Personal and Identification Information</h4>
          <p className="mb-2">We may collect:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Full name, date of birth, gender (where required), nationality, residential address, country of residence</li>
            <li>Email address, telephone/mobile number</li>
            <li>Identification document details (international passport, national ID, driver&apos;s licence or other government-issued ID)</li>
            <li>National Identification Number (NIN) and Bank Verification Number (BVN) where applicable and required for KYC</li>
            <li>Photograph or identity-document image captured for KYC verification</li>
            <li>Signature or electronic confirmation where applicable</li>
          </ul>
        </div>

        <div>
          <h4 className="font-semibold text-primary-500 mb-2">3.2 Account Information</h4>
          <p>
            Username or account identifier, email address, mobile number, password credentials (stored only in protected/hashed form), account status, customer category, KYC/verification status, and login/authentication records. Entrusted does not store plain-text passwords.
          </p>
        </div>

        <div>
          <h4 className="font-semibold text-primary-500 mb-2">3.3 Beneficiary Information</h4>
          <p>
            Beneficiary name, telephone number, email address (if provided), bank name, bank account number and related account details, beneficiary address (where required), relationship/purpose of transaction (where required), and any other information reasonably necessary to process, validate and complete the remittance.
          </p>
        </div>

        <div>
          <h4 className="font-semibold text-primary-500 mb-2">3.4 Transaction Information</h4>
          <p>
            Transaction reference, amount sent, currency, applicable exchange rate, Naira equivalent, fees and charges, sender and beneficiary details, payment and payout status, date and time, payment method, settlement information, transaction purpose, transaction history, and records of failed, reversed or cancelled transactions.
          </p>
        </div>

        <div>
          <h4 className="font-semibold text-primary-500 mb-2">3.5 Payment Information</h4>
          <p>
            When you fund a transaction through a third-party payment provider, Entrusted may receive confirmation data such as payment reference, payment status, masked card or account information, payment provider identity and transaction identifier. Entrusted does not intentionally store full card numbers, CVV/CVC codes or other sensitive authentication data unless expressly required and permitted under applicable payment-security standards (e.g., PCI-DSS).
          </p>
        </div>

        <div>
          <h4 className="font-semibold text-primary-500 mb-2">3.6 Device, Technical and Location Information</h4>
          <p>
            IP address, device type, operating system, browser or application version, device identifiers, mobile network information, login timestamps, application activity logs, error and diagnostic logs, and approximate location derived from network or device data. Precise location is requested only where strictly necessary for a specific service and where a lawful basis exists.
          </p>
        </div>
      </div>
    ),
  },
  {
    id: "section-4",
    num: "4",
    title: "How We Collect Information",
    content: (
      <div className="space-y-3">
        <p>We obtain personal data from the following sources:</p>
        <ul className="list-disc pl-5 space-y-2">
          <li>Directly from you during registration, KYC onboarding, beneficiary setup, transaction initiation and customer-support interactions</li>
          <li>Authorised identity-verification and KYC service providers</li>
          <li>Financial institutions, authorised dealer banks and payment service providers</li>
          <li>Fraud-prevention, sanctions-screening and AML monitoring providers</li>
          <li>Automatically through your use of the Platform (device and technical data)</li>
          <li>Publicly available or legally accessible sources where permitted by law</li>
        </ul>
      </div>
    ),
  },
  {
    id: "section-5",
    num: "5",
    title: "Why We Collect and Use Your Information",
    content: (
      <div className="space-y-3">
        <ol className="list-decimal pl-5 space-y-3">
          <li>
            <strong className="text-gray-900 font-semibold">Account Registration and Management</strong> – to create and maintain your account, verify contact details, authenticate identity and provide access to Services.
          </li>
          <li>
            <strong className="text-gray-900 font-semibold">Know Your Customer (KYC) and Customer Due Diligence</strong> – to verify identity, validate NIN/BVN and identification documents, detect duplicate or fraudulent registrations and meet regulatory obligations.
          </li>
          <li>
            <strong className="text-gray-900 font-semibold">Processing Remittance Transactions</strong> – to initiate and complete inbound remittances, apply exchange rates and fees, facilitate bank payouts, track status and issue receipts.
          </li>
          <li>
            <strong className="text-gray-900 font-semibold">AML/CFT/CPF and Financial Crime Prevention</strong> – to conduct sanctions and watchlist screening, detect suspicious activity, prevent fraud and money laundering, and comply with reporting obligations to the Nigeria Financial Intelligence Unit (NFIU) and CBN.
          </li>
          <li>
            <strong className="text-gray-900 font-semibold">Regulatory Compliance</strong> – to comply with requirements of the CBN, NDPC, NFIU and other competent authorities.
          </li>
          <li>
            <strong className="text-gray-900 font-semibold">Customer Support and Dispute Resolution</strong> – to respond to enquiries, investigate complaints, resolve transaction issues and verify account ownership.
          </li>
          <li>
            <strong className="text-gray-900 font-semibold">Security and Fraud Prevention</strong> – to detect unauthorised access, prevent account takeover, protect the Platform and investigate security incidents.
          </li>
          <li>
            <strong className="text-gray-900 font-semibold">Service Improvement</strong> – to analyse Platform performance, identify technical issues and develop new features. Analytical data is aggregated or anonymised where reasonably practicable.
          </li>
        </ol>
      </div>
    ),
  },
  {
    id: "section-6",
    num: "6",
    title: "Legal Basis for Processing",
    content: (
      <div className="space-y-3">
        <p>Entrusted processes personal data only where a lawful basis exists under the NDPA. Depending on the circumstances, the applicable bases include:</p>
        <ul className="list-disc pl-5 space-y-2">
          <li><strong className="text-gray-900 font-semibold">Performance of a contract</strong> – necessary to provide the remittance Services you request</li>
          <li><strong className="text-gray-900 font-semibold">Compliance with a legal obligation</strong> – including CBN, AML/CFT/CPF, NDPA and other regulatory requirements</li>
          <li><strong className="text-gray-900 font-semibold">Legitimate interests</strong> – where processing is necessary for Entrusted&apos;s legitimate interests (e.g., security, fraud prevention, service improvement) and does not override your rights and freedoms</li>
          <li><strong className="text-gray-900 font-semibold">Consent</strong> – where required by law (e.g., certain marketing communications or optional features). You may withdraw consent at any time without affecting the lawfulness of processing based on consent before its withdrawal.</li>
          <li><strong className="text-gray-900 font-semibold">Public interest / legal authority</strong> – where processing is required or authorised by law</li>
        </ul>
      </div>
    ),
  },
  {
    id: "section-7",
    num: "7",
    title: "KYC, BVN, NIN and Identity Verification",
    content: (
      <div className="space-y-4">
        <p>
          As a regulated IMTO, Entrusted is required to verify customer identity. We may use approved third-party verification services to validate BVN, NIN, passport or other identification data, bank account information and, for corporate customers, business registration details.
        </p>
        <p>
          Identity information is processed solely for legitimate purposes including customer onboarding, regulatory compliance, fraud prevention and transaction processing. Sensitive personal data (including biometric or identification numbers) is afforded enhanced protection.
        </p>
      </div>
    ),
  },
  {
    id: "section-8",
    num: "8",
    title: "Automated Decision-Making and Risk Screening",
    content: (
      <div className="space-y-4">
        <p>
          Entrusted users may use automated systems to assist with identity verification, fraud detection, transaction monitoring, sanctions screening, risk scoring, duplicate-account detection and identification of potentially suspicious transactions.
        </p>
        <p>
          Where applicable law grants rights in respect of solely automated decision-making that produces legal or similarly significant effects, Entrusted will provide appropriate safeguards and a mechanism for human review upon request.
        </p>
      </div>
    ),
  },
  {
    id: "section-9",
    num: "9",
    title: "Information Sharing",
    content: (
      <div className="space-y-4">
        <div className="p-4 bg-primary-500 text-white rounded-xl font-semibold flex items-center gap-3 shadow-sm">
          <svg className="w-5 h-5 text-accent-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
          <span className="text-white font-bold">Entrusted does not sell your personal data.</span>
        </div>
        <p>We may share personal data with:</p>
        <ul className="list-disc pl-5 space-y-2">
          <li><strong className="text-gray-900 font-semibold">Authorised Dealer Banks and Financial Institutions</strong> – to facilitate payment, settlement, beneficiary account validation and Naira payout.</li>
          <li><strong className="text-gray-900 font-semibold">Payment Service Providers</strong> – to process funding of transactions.</li>
          <li><strong className="text-gray-900 font-semibold">Identity Verification and KYC Providers</strong> – for identity validation.</li>
          <li><strong className="text-gray-900 font-semibold">AML, Sanctions and Fraud-Prevention Providers</strong> – for screening, monitoring and risk management.</li>
          <li><strong className="text-gray-900 font-semibold">Regulators and Competent Authorities</strong> – including CBN, NDPC, NFIU and law-enforcement agencies where required by law or lawful request.</li>
          <li><strong className="text-gray-900 font-semibold">Technology and Support Providers</strong> – cloud hosting, cybersecurity, infrastructure, customer-support, messaging, analytics and system-monitoring providers. Such providers receive only the data necessary to perform their contracted services and are bound by data-processing agreements.</li>
        </ul>
      </div>
    ),
  },
  {
    id: "section-10",
    num: "10",
    title: "International Data Transfers",
    content: (
      <div className="space-y-4">
        <p>
          Some of Entrusted&apos;s service providers or technology infrastructure may be located outside Nigeria.
        </p>
        <p>
          Where personal data is transferred outside Nigeria, Entrusted will ensure the transfer complies with the NDPA (including Sections 41 and 43) by using appropriate safeguards such as adequacy decisions, standard contractual clauses, binding corporate rules, or other legally permitted mechanisms, and by implementing technical and organisational measures to protect the data.
        </p>
      </div>
    ),
  },
  {
    id: "section-11",
    num: "11",
    title: "Data Security",
    content: (
      <div className="space-y-4">
        <p>
          Entrusted implements appropriate technical and organisational measures to protect personal data against unauthorised access, loss, theft, alteration, disclosure, destruction and unlawful processing. These measures include, as appropriate:
        </p>
        <ul className="list-disc pl-5 space-y-2">
          <li>Encryption in transit and at rest</li>
          <li>Access controls and role-based permissions</li>
          <li>Multi-factor authentication where implemented</li>
          <li>Secure API communications and audit logging</li>
          <li>Security monitoring, vulnerability management and incident response procedures</li>
          <li>Staff confidentiality obligations and training</li>
        </ul>
        <p className="text-xs text-gray-500 italic">
          No electronic transmission or storage system can be guaranteed to be completely secure. You are responsible for maintaining the confidentiality of your account credentials.
        </p>
      </div>
    ),
  },
  {
    id: "section-12",
    num: "12",
    title: "Data Retention",
    content: (
      <div className="space-y-4">
        <p>
          Entrusted retains personal data only for as long as reasonably necessary to fulfil the purposes for which it was collected, or for longer periods where required by law, regulation, contractual obligation or legitimate business need. In particular:
        </p>
        <ul className="list-disc pl-5 space-y-2">
          <li>
            <strong className="text-gray-900 font-semibold">Transaction and KYC records</strong> are generally retained for a minimum of five (5) years (or such longer period as may be required under applicable AML/CFT regulations, CBN directives or other legal obligations) after the end of the customer relationship or the relevant transaction.
          </li>
          <li>
            <strong className="text-gray-900 font-semibold">Account and support records</strong> are retained for the duration of the account relationship and a reasonable period thereafter for dispute resolution and audit purposes.
          </li>
        </ul>
        <p>
          When personal data is no longer required, Entrusted will securely delete, anonymise or otherwise dispose of it in accordance with applicable requirements and internal policies.
        </p>
      </div>
    ),
  },
  {
    id: "section-13",
    num: "13",
    title: "Cookies and Similar Technologies",
    content: (
      <div className="space-y-4">
        <p>
          The Entrusted website or Application may use cookies and similar technologies to maintain sessions, enhance security, remember preferences, analyse usage and improve performance.
        </p>
        <p>
          Where required, users will be provided with appropriate cookie notices and controls. Essential cookies necessary for the operation and security of the Platform cannot be disabled.
        </p>
      </div>
    ),
  },
  {
    id: "section-14",
    num: "14",
    title: "Your Data Protection Rights",
    content: (
      <div className="space-y-4">
        <p>Subject to applicable legal limitations and exemptions under the NDPA, you have the right to:</p>
        <ol className="list-decimal pl-5 space-y-2">
          <li>Request access to the personal data we hold about you</li>
          <li>Request correction of inaccurate or incomplete personal data</li>
          <li>Request deletion of personal data where legally permitted</li>
          <li>Request restriction of processing in certain circumstances</li>
          <li>Object to certain types of processing</li>
          <li>Request data portability where applicable</li>
          <li>Withdraw consent where processing is based on consent</li>
          <li>Request information about our processing activities</li>
          <li>Lodge a complaint with the Nigeria Data Protection Commission (NDPC)</li>
        </ol>
        <p className="text-sm text-primary-900 bg-primary-50 p-3.5 rounded-xl border border-primary-100 font-medium">
          Some rights may be limited where Entrusted is legally required to retain or process the information (for example, for AML/CFT record-keeping or ongoing regulatory investigations).
        </p>
      </div>
    ),
  },
  {
    id: "section-15",
    num: "15",
    title: "How to Exercise Your Rights",
    content: (
      <div className="space-y-4">
        <p>
          To exercise any of your rights or to raise a data-protection concern, contact Entrusted using the details in Section 25. We may need to verify your identity before processing certain requests in order to protect your account and personal data. We aim to respond to valid requests within the timeframes required by the NDPA.
        </p>
      </div>
    ),
  },
  {
    id: "section-16",
    num: "16",
    title: "Children's Privacy",
    content: (
      <div className="space-y-4">
        <p>
          The Entrusted Platform is not intended for individuals who are not legally permitted to use financial or remittance services under Nigerian law. We do not knowingly collect personal data from children in violation of applicable laws. If we become aware of such collection without an appropriate lawful basis, we will take reasonable steps to delete the data and address the situation.
        </p>
      </div>
    ),
  },
  {
    id: "section-17",
    num: "17",
    title: "Third-Party Websites and Services",
    content: (
      <div className="space-y-4">
        <p>
          The Platform may contain links to or integrations with third-party services (payment providers, banks, identity-verification services, government verification portals, etc.). Entrusted is not responsible for the privacy practices of independent third parties. You should review the privacy policies of those third parties before providing information to them.
        </p>
      </div>
    ),
  },
  {
    id: "section-18",
    num: "18",
    title: "Marketing Communications",
    content: (
      <div className="space-y-4">
        <p>
          Where legally permitted and where appropriate consent has been obtained, Entrusted may send service notifications, transaction alerts, security alerts, product updates and promotional communications. You may unsubscribe from marketing communications at any time via the unsubscribe link or by contacting us. Essential service communications (transaction confirmations, security alerts, regulatory notifications) will continue regardless of marketing preferences.
        </p>
      </div>
    ),
  },
  {
    id: "section-19",
    num: "19",
    title: "Transaction and Security Notifications",
    content: (
      <div className="space-y-4">
        <p>
          Entrusted may send notifications relating to registration, KYC status, login and security events, transaction initiation and completion, payment confirmation, payout, failed or reversed transactions, account security and material changes to terms or policies.
        </p>
      </div>
    ),
  },
  {
    id: "section-20",
    num: "20",
    title: "Data Breach and Security Incidents",
    content: (
      <div className="space-y-4">
        <p>
          When Entrusted becomes aware of a personal data breach that is likely to result in a risk to the rights and freedoms of data subjects, we will assess and respond to the incident in accordance with the NDPA.
        </p>
        <p>
          Where notification is legally required, Entrusted will notify the NDPC without undue delay and, where feasible, within 72 hours of becoming aware of the breach, and will notify affected individuals where required. Entrusted will also take reasonable steps to contain, investigate and remediate the incident.
        </p>
      </div>
    ),
  },
  {
    id: "section-21",
    num: "21",
    title: "Accuracy of Personal Information",
    content: (
      <div className="space-y-4">
        <p>
          You are responsible for ensuring that information you provide to Entrusted is accurate, complete and up to date. You should promptly notify us of any changes to your name, contact details, address, identification information or banking details. Providing inaccurate information may result in transaction delays, account restrictions or additional verification requirements.
        </p>
      </div>
    ),
  },
  {
    id: "section-22",
    num: "22",
    title: "Account Security",
    content: (
      <div className="space-y-4">
        <p>
          You are responsible for maintaining the confidentiality of your account credentials. Keep your password confidential, never share OTPs or authentication codes, use secure devices, report any suspected unauthorized access immediately, and log out from shared devices. Entrusted will never ask you to disclose your password or security PIN through unsolicited communication.
        </p>
      </div>
    ),
  },
  {
    id: "section-23",
    num: "23",
    title: "Changes to This Privacy Policy",
    content: (
      <div className="space-y-4">
        <p>
          Entrusted may update this Privacy Policy from time to time to reflect changes in services, technology, regulatory developments, applicable legislation or data-processing practices. Where material changes are made, we will notify users through the Platform, website, email or other appropriate channels.
        </p>
        <p>
          The updated policy will indicate the date it was last revised. Continued use of the Platform after the effective date of an updated policy constitutes acceptance of the revised terms.
        </p>
      </div>
    ),
  },
  {
    id: "section-24",
    num: "24",
    title: "Complaints",
    content: (
      <div className="space-y-4">
        <p>
          If you believe that Entrusted has processed your personal data improperly, please contact us first so that we can investigate and address your concern. You also have the right to lodge a complaint with the Nigeria Data Protection Commission (NDPC) in accordance with the NDPA.
        </p>
      </div>
    ),
  },
  {
    id: "section-25",
    num: "25",
    title: "Contact Us",
    content: (
      <div className="space-y-4">
        <div className="bg-primary-500 text-white p-6 sm:p-8 rounded-2xl space-y-4 shadow-md">
          <h4 className="font-bold text-2xl text-white">Entrusted Limited</h4>
          <p className="text-white/90 font-medium">Data Protection / Privacy Team</p>
          <div className="grid sm:grid-cols-2 gap-4 text-sm pt-4 border-t border-white/20 text-white">
            <div>
              <span className="block text-white/80 font-medium">Email:</span>
              <a href="mailto:privacy@entrusted.com" className="font-semibold underline hover:text-accent-500 text-white">privacy@entrusted.com</a>
            </div>
            <div>
              <span className="block text-white/80 font-medium">Website:</span>
              <a href="https://entrusted.com" target="_blank" rel="noreferrer" className="font-semibold underline hover:text-accent-500 text-white">www.entrusted.com</a>
            </div>
            <div>
              <span className="block text-white/80 font-medium">Phone:</span>
              <span className="font-semibold text-white">+234 708 579 1804</span>
            </div>
            <div>
              <span className="block text-white/80 font-medium">Address:</span>
              <span className="font-semibold text-white">Plot 1014 Fria Close Wuse II Abuja, Nigeria</span>
            </div>
          </div>
          <div className="pt-4 border-t border-white/20 text-white">
            <h5 className="font-bold text-white text-base">Data Protection Officer (DPO)</h5>
            <p className="text-sm text-white/90">Email: <a href="mailto:dpo@entrusted.com" className="underline font-semibold text-white hover:text-accent-500">dpo@entrusted.com</a></p>
          </div>
        </div>
      </div>
    ),
  },
  {
    id: "section-26",
    num: "26",
    title: "Acceptance",
    content: (
      <div className="space-y-4">
        <p>
          By creating an account or using the Entrusted Remittance Platform, you acknowledge that you have read and understood this Privacy Policy and understand how Entrusted may collect, use, disclose and protect your personal data as described above. Where applicable, Entrusted will obtain separate consent for processing activities for which consent is legally required.
        </p>

        {/* In-App Consent Notice Banner */}
        <div className="mt-8 border-2 border-primary-500 rounded-2xl p-6 bg-white shadow-sm">
          <h4 className="font-bold text-lg text-primary-500 mb-3">Recommended In-App Privacy Consent</h4>
          <div className="bg-gray-50 p-4 rounded-xl border border-gray-200 text-sm text-gray-700 leading-relaxed mb-4">
            <strong>Privacy Notice:</strong> By continuing, you acknowledge that Entrusted Limited will collect and process your personal data for account registration, identity verification, transaction processing, fraud prevention, regulatory compliance (including AML/CFT/CPF) and other purposes described in our Privacy Policy.
          </div>
          <div className="flex items-center gap-3 text-sm font-medium text-gray-900">
            <input type="checkbox" checked disabled className="h-4 w-4 rounded accent-primary-500 cursor-not-allowed" />
            <span>I have read and agree to the Privacy Policy.</span>
          </div>
          <div className="mt-4 flex gap-4 text-xs font-semibold text-primary-500">
            <span className="underline cursor-pointer">Privacy Policy</span>
            <span>|</span>
            <Link to="/terms-of-use" className="underline">Terms of Use</Link>
          </div>
        </div>

        {/* Implementation Points Table */}
        <div className="mt-8">
          <h4 className="font-bold text-lg text-gray-900 mb-4">Recommended Implementation Points</h4>
          <div className="overflow-x-auto rounded-xl border border-gray-200 shadow-sm">
            <table className="w-full text-left text-sm">
              <thead className="bg-primary-500 text-white font-bold border-b border-primary-600">
                <tr>
                  <th className="p-3 sm:p-4 text-white font-bold">Location</th>
                  <th className="p-3 sm:p-4 text-white font-bold">Implementation</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                <tr>
                  <td className="p-3 sm:p-4 font-semibold text-gray-900">Registration</td>
                  <td className="p-3 sm:p-4 text-gray-700">Mandatory acknowledgement / acceptance of Privacy Policy</td>
                </tr>
                <tr>
                  <td className="p-3 sm:p-4 font-semibold text-gray-900">Application Settings</td>
                  <td className="p-3 sm:p-4 text-gray-700">Privacy & Data section with link to full policy</td>
                </tr>
                <tr>
                  <td className="p-3 sm:p-4 font-semibold text-gray-900">Website / Footer</td>
                  <td className="p-3 sm:p-4 text-gray-700">Full Privacy Policy link</td>
                </tr>
                <tr>
                  <td className="p-3 sm:p-4 font-semibold text-gray-900">KYC Screen</td>
                  <td className="p-3 sm:p-4 text-gray-700">Short KYC-specific privacy notice</td>
                </tr>
                <tr>
                  <td className="p-3 sm:p-4 font-semibold text-gray-900">Transaction Screen</td>
                  <td className="p-3 sm:p-4 text-gray-700">Transaction-data processing notice</td>
                </tr>
                <tr>
                  <td className="p-3 sm:p-4 font-semibold text-gray-900">Cookie-enabled Web Portal</td>
                  <td className="p-3 sm:p-4 text-gray-700">Cookie notice and preference controls</td>
                </tr>
                <tr>
                  <td className="p-3 sm:p-4 font-semibold text-gray-900">Customer Support</td>
                  <td className="p-3 sm:p-4 text-gray-700">Dedicated privacy request / complaint channel</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    ),
  },
];

const PrivacyPolicy = () => {
  const [searchTerm, setSearchTerm] = useState("");

  const filteredSections = useMemo(() => {
    if (!searchTerm.trim()) return privacySections;
    const term = searchTerm.toLowerCase();
    return privacySections.filter(
      (s) =>
        s.title.toLowerCase().includes(term) ||
        s.num.includes(term)
    );
  }, [searchTerm]);

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <Main>
      <Helmet>
        <title>Privacy Policy | Entrusted Limited</title>
        <meta
          name="description"
          content="Privacy Policy for Entrusted Limited Remittance Application & Platform, issued in accordance with Nigeria Data Protection Act 2023 (NDPA) and CBN Guidelines."
        />
      </Helmet>

      <div className="bg-[#f6f6f6] min-h-screen pt-[6rem] sm:pt-[8rem] pb-[6rem]">
        {/* Hero Header */}
        <div className="bg-gradient-to-r from-primary-500 via-blue-700 to-secondary-500 text-white py-12 sm:py-16 mb-10 shadow-lg">
          <div className="container px-4 mx-auto">
            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md px-4 py-1.5 rounded-full text-xs font-semibold tracking-wider text-accent-500 border border-white/20 mb-6">
                <span>Version 2.0 (IMTO Standard)</span>
                <span>•</span>
                <span>NDPA 2023 Compliant</span>
              </div>
              <h1 className="text-3xl sm:text-5xl font-bold tracking-tight mb-4 text-white">
                ENTRUSTED LIMITED PRIVACY POLICY
              </h1>
              <p className="text-lg sm:text-xl font-light text-white/90 mb-6">
                Entrusted Remittance Application / Platform
              </p>
              
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs sm:text-sm text-white max-w-3xl mx-auto pt-6 border-t border-white/15">
                <div>
                  <span className="block font-semibold text-white">Effective Date</span>
                  <span className="text-white/90">4 September 2026</span>
                </div>
                <div>
                  <span className="block font-semibold text-white">Last Updated</span>
                  <span className="text-white/90">4 September 2026</span>
                </div>
                <div>
                  <span className="block font-semibold text-white">Jurisdiction</span>
                  <span className="text-white/90">Nigeria</span>
                </div>
                <div>
                  <span className="block font-semibold text-white">Regulatory Standard</span>
                  <span className="text-white/90">CBN & NDPC</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Regulatory Preamble Box */}
        <div className="container px-4 mx-auto mb-10">
          <div className="max-w-6xl mx-auto bg-white p-6 sm:p-8 rounded-2xl shadow-sm border border-gray-200">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-primary-500 text-white rounded-xl hidden sm:block">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <div className="text-sm sm:text-base text-gray-700 leading-relaxed">
                <strong className="text-gray-900 font-semibold block mb-1">Regulatory Compliance Notice:</strong>
                This Privacy Policy is issued in accordance with the <strong>Nigeria Data Protection Act, 2023 (NDPA)</strong>, the <strong>Nigeria Data Protection Commission (NDPC)</strong> General Application and Implementation Directive, applicable <strong>Central Bank of Nigeria (CBN)</strong> Guidelines for International Money Transfer Operators, and other relevant Nigerian laws and regulations.
              </div>
            </div>
          </div>
        </div>

        {/* Main Content Area */}
        <div className="container px-4 mx-auto">
          <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-8">
            
            {/* Dark Sleek Sidebar / Navigation */}
            <div className="lg:col-span-4">
              <div className="sticky top-28 bg-secondary-500 text-white p-5 sm:p-6 rounded-2xl shadow-xl border border-primary-500/30 max-h-[80vh] flex flex-col">
                <h3 className="font-bold text-white text-lg mb-3 flex items-center justify-between">
                  <span className="text-white">Table of Contents</span>
                  <span className="text-xs bg-white/20 text-white px-3 py-1 rounded-full font-bold">26 Sections</span>
                </h3>

                {/* Quick Search input */}
                <div className="mb-4">
                  <input
                    type="text"
                    placeholder="Search section..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full text-xs px-3.5 py-2.5 rounded-xl bg-white/10 text-white placeholder-white/70 border border-white/20 focus:outline-none focus:border-white focus:bg-white/20 transition-all"
                  />
                </div>

                <nav className="space-y-1 text-xs overflow-y-auto pr-1 flex-1">
                  {filteredSections.map((sec) => (
                    <button
                      key={sec.id}
                      onClick={() => scrollToSection(sec.id)}
                      className="w-full text-left py-2.5 px-3 rounded-xl hover:bg-primary-500 text-white transition-all font-medium whitespace-normal break-words leading-normal block border border-transparent hover:border-white/20"
                    >
                      <span className="font-bold text-accent-500 mr-2 text-sm">{sec.num}.</span>
                      <span className="text-white font-medium text-xs sm:text-sm">{sec.title}</span>
                    </button>
                  ))}
                  {filteredSections.length === 0 && (
                    <p className="text-white/60 text-xs py-3 text-center">No matching sections found.</p>
                  )}
                </nav>
              </div>
            </div>

            {/* Content Sections */}
            <div className="lg:col-span-8 space-y-6">
              {filteredSections.map((sec) => (
                <div
                  key={sec.id}
                  id={sec.id}
                  className="bg-white p-6 sm:p-8 rounded-2xl shadow-sm border border-gray-200 hover:shadow-md transition-shadow scroll-mt-28"
                >
                  <div className="flex items-center gap-3 mb-4 pb-3 border-b border-gray-100">
                    <span className="flex items-center justify-center w-8 h-8 rounded-xl bg-primary-500 text-white font-bold text-sm">
                      {sec.num}
                    </span>
                    <h2 className="text-xl sm:text-2xl font-bold text-gray-900">
                      {sec.title}
                    </h2>
                  </div>
                  <div className="text-gray-700 text-sm sm:text-base leading-relaxed">
                    {sec.content}
                  </div>
                </div>
              ))}

              {/* End of Policy Footer */}
              <div className="text-center py-8 bg-white rounded-2xl border border-gray-200 p-6">
                <p className="text-gray-500 font-medium text-sm">
                  — End of Privacy Policy —
                </p>
                <p className="text-xs text-gray-400 mt-2">
                  © Entrusted Limited. All Rights Reserved.
                </p>
              </div>
            </div>

          </div>
        </div>
      </div>
    </Main>
  );
};

export default PrivacyPolicy;
